import { request } from "@umijs/max";
import { abortControllers, AbortControllerType } from "./constants";
import {
  WechatConversationDataType,
  WechatContact,
  WS_REQUEST_MESSAGE_TYPE,
  WechatRoom,
  ServiceType,
  ChannelTypeEnum,
  WechatMessageType,
} from "@packages/chat-sdk";
import { nanoid } from "nanoid";

/**
 * 获取置顶会话列表
 *
 */
export type GetTopConversationListParams = {
  fromUserId?: string;
  teamId?: string;
  name?: string;
  unRead?: boolean;
  robotStatus?: boolean;
};
export function getConversationTopListService(
  params: GetTopConversationListParams
) {
  const controller = new AbortController();
  const oldController = abortControllers.get(
    AbortControllerType.CONVERSATION_TOP_LIST
  );
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.CONVERSATION_TOP_LIST, controller);

  return request<API.BaseResponse<WechatConversationDataType[]>>(
    "/api/damselfish-chat-bot/conversation/topList",
    {
      method: "GET",
      signal: controller.signal,
      params: {
        ...params,
        channelType: ChannelTypeEnum.Wechat,
      },
    }
  );
}

/**
 * 获取会话列表
 *
 */

export type GetConversationListParams = {
  fromUserId?: string;
  teamId?: string;
  name?: string;
  unRead?: boolean;
  pageNo?: number;
  pageSize?: number;
  robotStatus?: boolean;
};

export function getConversationListService(params: GetConversationListParams) {
  const controller = new AbortController();
  const oldController = abortControllers.get(
    AbortControllerType.CONVERSATION_LIST
  );
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.CONVERSATION_LIST, controller);
  return request<
    API.BaseResponse<{ list: WechatConversationDataType[]; total: number }>
  >("/api/damselfish-chat-bot/conversation/page", {
    method: "GET",
    signal: controller.signal,
    params: {
      ...params,
      channelType: ChannelTypeEnum.Wechat,
    },
  });
}

/**
 * 一键清除未读消息
 * */
export function clearUnreadService(teamId?: string) {
  return request<API.BaseResponse<boolean>>(
    "/api/damselfish-chat-bot/conversation/unread",
    {
      method: "POST",
      params: {
        teamId,
        channelType: ChannelTypeEnum.Wechat,
      },
    }
  );
}

/**
 *
 * 更改会话状态
 */
type ToggleConversationStatusParams = {
  chat_id: string;
  is_top?: number;
  is_shield?: number;
  unread?: number;
};
type ToggleConversationStatusData = {
  data: {
    chat_id: string;
    is_top?: number;
    is_shield?: number;
    unread?: number;
  };
};

export function toggleConversationStatusService(
  fromUserId: string,
  params: ToggleConversationStatusParams
) {
  return request<API.BaseResponse<ToggleConversationStatusData>>(
    "/api/damselfish-chat-bot/command?UPDATE_CONVERSATION_STATUS",
    {
      method: "POST",
      data: {
        message_type: WS_REQUEST_MESSAGE_TYPE.UPDATE_CONVERSATION_STATUS,
        data: params,
        user_id: fromUserId,
        channel_type: ChannelTypeEnum.Wechat,
      },
    }
  );
}

/**
 * 创建会话
 *
 */
type CreateConversationParams = {
  userId: string;
  otherId: string;
};
export function createConversationService(params: CreateConversationParams) {
  return request<API.BaseResponse<WechatConversationDataType>>(
    "/api/damselfish-chat-bot/conversation/create",
    {
      method: "POST",
      data: {
        ...params,
        channelType: ChannelTypeEnum.Wechat,
      },
    }
  );
}

/**
 * 查询会话
 */
type FindConversationParams = {
  userId: string;
  conversationId: string;
};
export function findConversationService(params: FindConversationParams) {
  return request<API.BaseResponse<WechatConversationDataType>>(
    "/api/damselfish-chat-bot/conversation/findById",
    {
      method: "POST",
      data: {
        ...params,
        channelType: ChannelTypeEnum.Wechat,
      },
    }
  );
}

/**
 * 查询外部联系人详情
 */
export function getContactDetailService(userId: string, fromUserId?: string) {
  const controller = new AbortController();
  const oldController = abortControllers.get(
    AbortControllerType.CONTACT_DETAIL
  );
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.CONTACT_DETAIL, controller);
  return request<API.BaseResponse<WechatContact>>(
    "/api/damselfish-chat-bot/user/detail",
    {
      method: "GET",
      signal: controller.signal,
      params: {
        userId,
        fromUserId,
        channelType: ChannelTypeEnum.Wechat,
      },
    }
  );
}

/**
 * 查询群详情
 */
type GetRoomDetailParams = {
  userId: string;
  roomId: string;
};
export function getRoomDetailService(params: GetRoomDetailParams) {
  const controller = new AbortController();
  const oldController = abortControllers.get(AbortControllerType.ROOM_DETAIL);
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.ROOM_DETAIL, controller);
  return request<API.BaseResponse<WechatRoom>>(
    "/api/damselfish-chat-bot/room/detail",
    {
      method: "GET",
      signal: controller.signal,
      params: {
        ...params,
        channelType: ChannelTypeEnum.Wechat,
      },
    }
  );
}

/**
 *
 * 获取历史消息
 */
export function getHistoryMessageListService(data: {
  chatId: string;
  lastMsgTime?: string;
  pageNo?: number;
  pageSize?: number;
}) {
  const controller = new AbortController();
  const oldController = abortControllers.get(
    AbortControllerType.MESSAGE_HISTORY
  );
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.MESSAGE_HISTORY, controller);
  return request<
    API.BaseResponse<{
      records: WechatMessageType[];
      total: number;
    }>
  >("/api/damselfish-chat-bot/history/page", {
    method: "POST",
    signal: controller.signal,
    data: {
      ...data,
      channelType: ChannelTypeEnum.Wechat,
    },
  });
}

export type AddFriendByBusinessCardParams = {
  /** 如果来自群，则需要填入群ID，不需要带 R:; 如果来自好友，这个字段填0就行(后端说不用传） */
  from_room_id?: string;
  /** 推荐者ID */
  presenter_id: string;
  /** 添加的ID */
  user_id: string;
  /** 验证文本 */
  verify_text: string;
};

/** 添加名片分享的好友 */
export function addFriendByBusinessCardService(
  from_user_id: string,
  service_type: ServiceType,
  data: AddFriendByBusinessCardParams
) {
  return request<API.BaseResponse<boolean>>(
    "/api/damselfish-chat-bot/command?ADD_FRIEND_BY_BUSINESS_CARD",
    {
      method: "POST",
      data: {
        user_id: from_user_id,
        service_type,
        message_type: WS_REQUEST_MESSAGE_TYPE.ADD_FRIEND_BY_BUSINESS_CARD,
        data,
        request_key: nanoid(),
        channel_type: ChannelTypeEnum.Wechat,
      },
    }
  );
}
